import React, { useEffect, useState, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { CSSTransition } from 'react-transition-group'
import style from '@aytm/app/swidget/questions/styles/common/magnifier.module.scss'

interface IMagnifierProps {
  cursor: React.ReactNode
  imageSrc: string
  externalClass?: string
  imageWidth: number
  imageHeight: number
  imageX: number
  imageY: number
  show: boolean
  pageX: number
  pageY: number
  height: number
  width: number
  zoom: number
  fixed: boolean
  element?: HTMLElement | null
  children?: React.ReactNode
}

const Magnifier = ({
  children,
  cursor,
  imageSrc,
  imageWidth,
  imageHeight,
  imageX,
  imageY,
  show,
  pageX,
  pageY,
  height,
  width,
  zoom,
  externalClass,
  fixed = false,
  element = null
}: IMagnifierProps) => {
  const [dimensions, setDimensions] = useState<{ width: number; height: number }>({
    width: 0,
    height: 0
  })

  const onShow = useCallback(() => {
    setDimensions({ width, height })
  }, [width, height])

  const onHide = useCallback(() => {
    setDimensions({ width: 0, height: 0 })
  }, [])

  useEffect(() => {
    if (show) {
      onShow()
    } else {
      onHide()
    }
  }, [show, onShow, onHide])

  const magnifierPositionStyle = fixed
    ? {
        width: `${dimensions.width}px`,
        height: `${dimensions.height}px`,
        top: pageY,
        left: pageX,
        position: 'fixed' as const
      }
    : {
        width: `${dimensions.width}px`,
        height: `${dimensions.height}px`,
        top: pageY - dimensions.height,
        left: pageX - dimensions.width / 2
      }

  const magnifierContentStyle = {
    backgroundImage: `url(${imageSrc})`,
    backgroundSize: `${imageWidth * zoom}px ${imageHeight * zoom}px`,
    backgroundPosition: `0 0`,
    height: `${imageHeight * zoom}px`,
    width: `${imageWidth * zoom}px`,
    left: `${-(imageX * zoom - dimensions.width / 2)}px`,
    top: `${-(imageY * zoom - dimensions.height / 2)}px`
  }

  return createPortal(
    <div
      className={`${style.component} ${externalClass ? externalClass : ''} ${fixed ? style.fixed : style.floating}`}
      style={magnifierPositionStyle}
    >
      <CSSTransition in={show} timeout={200} classNames="swidget-magnifier-animate">
        <div className={style.animate}>
          <div className={style.content} style={magnifierContentStyle}>
            {children}
          </div>
          {cursor}
        </div>
      </CSSTransition>
    </div>,
    element || document.body
  )
}

export default Magnifier
